/**
 * vibecheck reality — Runtime verification with Playwright
 *
 * Modes:
 *   test    — Standard runtime checks (homepage, console, links, images, API)
 *   chaos   — Chaos Agent: random clicks, invalid inputs, rapid navigation, error injection
 *   stress  — Stress test: concurrent users, rapid requests, memory/CPU monitoring
 *   list    — List available flows and modes
 */

import * as fs from 'fs';
import * as path from 'path';
import type { Command } from 'commander';
import ora from 'ora';
import type { GlobalOptions } from '../types';
import { ensureVibecheckDir } from '../core/config';
import * as out from '../ui/output';
import { colors, icons } from '../ui/theme';

// ── Types ───────────────────────────────────────────────────────────────────

interface RealityOpts extends GlobalOptions {
  url?: string;
  headed?: boolean;
  flow?: string;
  timeout?: string;
  screenshot?: boolean;
  report?: boolean;
  recordVideo?: boolean;
  actions?: string;
  concurrency?: string;
  duration?: string;
  seed?: string;
}

interface RealityCheck {
  name: string;
  status: 'passed' | 'failed' | 'warning';
  duration: number;
  message?: string;
  screenshot?: string;
}

interface ChaosAction {
  type: string;
  target?: string;
  result: 'survived' | 'crashed' | 'error' | 'degraded';
  message: string;
  duration: number;
}

interface StressResult {
  name: string;
  status: 'passed' | 'failed' | 'warning';
  value: string;
  duration: number;
}

// ── Registration ────────────────────────────────────────────────────────────

export function registerReality(program: Command): void {
  const cmd = program
    .command('reality')
    .description('Runtime verification — test, chaos, and stress modes')
    .option('--url <url>', 'Base URL to test against', 'http://localhost:3000')
    .option('--headless', 'Run in headless mode (default)', true)
    .option('--headed', 'Run with visible browser')
    .option('--flow <name>', 'Run a specific flow only')
    .option('--timeout <ms>', 'Timeout per check in ms', '30000')
    .option('--screenshot', 'Capture screenshots on failure')
    .option('--record-video', 'Record browser session as video')
    .option('--report', 'Generate HTML report');

  // ── Standard test mode ────────────────────────────────────────────────────

  cmd.command('test').description('Run standard reality checks (homepage, console, links, images, API)').action(async (_o: any, c: Command) => {
    await runReality({ ...(c.parent!.parent!.opts() as GlobalOptions), ...(c.parent!.opts() as any) });
  });

  // ── Chaos mode ────────────────────────────────────────────────────────────

  cmd.command('chaos')
    .description('Chaos Agent — random clicks, invalid inputs, rapid navigation, error injection')
    .option('--actions <n>', 'Number of chaos actions to perform', '20')
    .option('--seed <n>', 'Random seed for reproducible chaos')
    .action(async (chaosOpts: any, c: Command) => {
      const globals = c.parent!.parent!.opts() as GlobalOptions;
      const parentOpts = c.parent!.opts() as any;
      await runChaos({ ...globals, ...parentOpts, ...chaosOpts });
    });

  // ── Stress mode ───────────────────────────────────────────────────────────

  cmd.command('stress')
    .description('Stress test — concurrent requests, rapid navigation, resource monitoring')
    .option('--concurrency <n>', 'Number of concurrent virtual users', '10')
    .option('--duration <s>', 'Test duration in seconds', '30')
    .action(async (stressOpts: any, c: Command) => {
      const globals = c.parent!.parent!.opts() as GlobalOptions;
      const parentOpts = c.parent!.opts() as any;
      await runStress({ ...globals, ...parentOpts, ...stressOpts });
    });

  // ── List mode ─────────────────────────────────────────────────────────────

  cmd.command('list').description('List available reality modes and flows').action(async (_o: any, c: Command) => {
    await runList(c.parent!.parent!.opts() as GlobalOptions);
  });

  // Default action
  cmd.action(async (opts: any, command: Command) => {
    await runReality({ ...(command.parent!.opts() as GlobalOptions), ...opts });
  });
}

// ── Playwright Helper ───────────────────────────────────────────────────────

function checkPlaywright(): boolean {
  try { require.resolve('playwright'); return true; } catch {
    try { require.resolve('@playwright/test'); return true; } catch { return false; }
  }
}

function showPlaywrightInstall(): void {
  out.warn('Playwright is not installed. Reality Mode requires Playwright for browser testing.');
  out.nl();
  console.log(`  Install it with: ${colors.info('npm install -D playwright @playwright/test')}`);
  console.log(`  Then run: ${colors.info('npx playwright install chromium')}`);
  out.nl();
}

// ═══════════════════════════════════════════════════════════════════════════
// STANDARD TEST MODE
// ═══════════════════════════════════════════════════════════════════════════

async function runReality(opts: RealityOpts): Promise<void> {
  const start = performance.now();
  const rootPath = opts.path || process.cwd();
  const baseUrl = opts.url || 'http://localhost:3000';

  out.brandHeader('Reality Mode', 'Runtime Verification');
  out.nl();

  if (!checkPlaywright()) {
    showPlaywrightInstall();
    out.info('Running HTTP-only checks (no browser)...');
    out.nl();
    await runHttpChecks(baseUrl, opts);
    return;
  }

  // Set up video recording directory
  let videosDir: string | undefined;
  if (opts.recordVideo) {
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').slice(0, 19);
    const realityDir = path.join(ensureVibecheckDir(rootPath), 'reality', stamp);
    videosDir = path.join(realityDir, 'videos');
    if (!fs.existsSync(videosDir)) fs.mkdirSync(videosDir, { recursive: true });
  }

  const spinner = ora(`Testing against ${colors.info(baseUrl)}`).start();
  const checks: RealityCheck[] = [];

  try {
    const { chromium } = require('playwright');
    const browser = await chromium.launch({ headless: !opts.headed });
    const contextOpts: Record<string, any> = {};
    if (videosDir) {
      contextOpts.recordVideo = { dir: videosDir, size: { width: 1280, height: 720 } };
    }
    const context = await browser.newContext(contextOpts);
    const page = await context.newPage();
    const timeout = parseInt(opts.timeout || '30000');

    // Check 1: Homepage loads
    const t1 = performance.now();
    try {
      spinner.text = 'Checking homepage loads...';
      const response = await page.goto(baseUrl, { timeout, waitUntil: 'domcontentloaded' });
      const status = response?.status() || 0;
      checks.push({ name: 'Homepage loads', status: status >= 200 && status < 400 ? 'passed' : 'failed', duration: performance.now() - t1, message: status >= 400 ? `HTTP ${status}` : undefined });
    } catch (e: any) {
      checks.push({ name: 'Homepage loads', status: 'failed', duration: performance.now() - t1, message: e.message?.slice(0, 100) });
    }

    // Check 2: Console errors
    const consoleErrors: string[] = [];
    page.on('console', (msg: any) => { if (msg.type() === 'error') consoleErrors.push(msg.text()); });
    const t2 = performance.now();
    try {
      spinner.text = 'Checking for console errors...';
      await page.goto(baseUrl, { timeout, waitUntil: 'networkidle' });
      checks.push({ name: 'No console errors', status: consoleErrors.length === 0 ? 'passed' : 'warning', duration: performance.now() - t2, message: consoleErrors.length > 0 ? `${consoleErrors.length} errors` : undefined });
    } catch {
      checks.push({ name: 'No console errors', status: 'warning', duration: performance.now() - t2, message: 'Could not fully load page' });
    }

    // Check 3: Navigation links
    const t3 = performance.now();
    try {
      spinner.text = 'Checking navigation links...';
      const links = await page.$$eval('a[href]', (els: any[]) => els.map((el: any) => el.getAttribute('href')).filter((h: string) => h && !h.startsWith('#') && !h.startsWith('mailto:') && !h.startsWith('tel:')));
      const internalLinks = links.filter((l: string) => l.startsWith('/') || l.startsWith(baseUrl));
      checks.push({ name: 'Navigation links', status: 'passed', duration: performance.now() - t3, message: `${internalLinks.length} internal links found` });
    } catch {
      checks.push({ name: 'Navigation links', status: 'warning', duration: performance.now() - t3, message: 'Could not check links' });
    }

    // Check 4: Images load
    const t4 = performance.now();
    try {
      spinner.text = 'Checking images...';
      const images = await page.$$eval('img[src]', (imgs: any[]) => imgs.map((img: any) => ({ src: img.src, loaded: img.complete && img.naturalHeight > 0 })));
      const broken = images.filter((i: any) => !i.loaded).length;
      checks.push({ name: 'Images', status: broken === 0 ? 'passed' : 'warning', duration: performance.now() - t4, message: `${images.length - broken}/${images.length} loaded` });
    } catch {
      checks.push({ name: 'Images', status: 'passed', duration: performance.now() - t4, message: 'check skipped' });
    }

    // Check 5: API health endpoint
    const t5 = performance.now();
    try {
      spinner.text = 'Checking API health...';
      const healthResponse = await page.request.get(`${baseUrl}/api/health`);
      const hs = healthResponse.status();
      checks.push({ name: 'API /api/health', status: hs === 200 ? 'passed' : hs === 404 ? 'warning' : 'failed', duration: performance.now() - t5, message: `${hs} ${hs === 200 ? 'OK' : hs === 404 ? 'Not Found' : 'Error'}` });
    } catch {
      checks.push({ name: 'API /api/health', status: 'warning', duration: performance.now() - t5, message: 'endpoint not reachable' });
    }

    // Screenshots on failure
    if (opts.screenshot) {
      const screenshotDir = path.join(ensureVibecheckDir(rootPath), 'screenshots');
      if (!fs.existsSync(screenshotDir)) fs.mkdirSync(screenshotDir, { recursive: true });
      for (const check of checks) {
        if (check.status === 'failed') {
          const ssPath = path.join(screenshotDir, `${check.name.replace(/\s+/g, '-').toLowerCase()}.png`);
          try { await page.screenshot({ path: ssPath, fullPage: true }); check.screenshot = ssPath; } catch { /* ignore */ }
        }
      }
    }

    // Save video before closing (Playwright finalizes video on context close)
    if (videosDir) {
      const video = page.video();
      if (video) {
        try { await video.saveAs(path.join(videosDir, 'reality-session.webm')); } catch { /* video may already be saved */ }
      }
    }
    await context.close();
    await browser.close();

    if (videosDir) {
      try {
        const vids = fs.readdirSync(videosDir).filter((f: string) => f.endsWith('.webm') || f.endsWith('.mp4'));
        if (vids.length > 0) {
          out.nl();
          out.info(`Video recorded: ${path.relative(rootPath, path.join(videosDir, vids[0]))}`);
        }
      } catch { /* ignore */ }
    }
  } catch (e: any) {
    spinner.fail('Browser test failed');
    out.error(e.message?.slice(0, 200) || 'Unknown error');
    out.info('Falling back to HTTP-only checks...');
    await runHttpChecks(baseUrl, opts);
    return;
  }

  spinner.stop();
  const duration = performance.now() - start;

  if (opts.json) { out.jsonOutput({ mode: 'test', checks, duration, url: baseUrl }); process.exitCode = checks.some(c => c.status === 'failed') ? 2 : 0; return; }

  renderChecks(checks, baseUrl, duration);
}

// ═══════════════════════════════════════════════════════════════════════════
// CHAOS MODE — Random clicks, invalid inputs, rapid navigation
// ═══════════════════════════════════════════════════════════════════════════

async function runChaos(opts: RealityOpts): Promise<void> {
  const start = performance.now();
  const rootPath = opts.path || process.cwd();
  const baseUrl = opts.url || 'http://localhost:3000';
  const maxActions = parseInt(opts.actions || '20');
  const seed = opts.seed ? parseInt(opts.seed) : Date.now();

  out.brandHeader('Chaos Agent', 'Resilience Testing');
  out.nl();
  console.log(`  ${colors.dim('Target:')}      ${colors.info(baseUrl)}`);
  console.log(`  ${colors.dim('Actions:')}     ${maxActions}`);
  console.log(`  ${colors.dim('Seed:')}        ${seed}`);
  out.nl();

  if (!checkPlaywright()) {
    showPlaywrightInstall();
    out.info('Running HTTP chaos (no browser)...');
    out.nl();
    await runHttpChaos(baseUrl, maxActions, opts);
    return;
  }

  const spinner = ora('Launching Chaos Agent...').start();
  const actions: ChaosAction[] = [];
  const rng = seededRandom(seed);

  try {
    const { chromium } = require('playwright');
    const browser = await chromium.launch({ headless: !opts.headed });
    const context = await browser.newContext();
    const page = await context.newPage();
    const timeout = parseInt(opts.timeout || '15000');

    // Navigate to base URL first
    await page.goto(baseUrl, { timeout, waitUntil: 'domcontentloaded' });

    const CHAOS_ACTIONS = [
      'random-click', 'invalid-input', 'rapid-navigate', 'resize-viewport',
      'disable-js', 'clear-storage', 'back-forward', 'double-click',
      'right-click', 'keyboard-spam', 'scroll-extreme', 'network-offline',
    ];

    for (let i = 0; i < maxActions; i++) {
      const actionType = CHAOS_ACTIONS[Math.floor(rng() * CHAOS_ACTIONS.length)];
      spinner.text = `Chaos action ${i + 1}/${maxActions}: ${actionType}...`;
      const t = performance.now();

      try {
        switch (actionType) {
          case 'random-click': {
            const elements = await page.$$('a, button, input, [role="button"], [onclick]');
            if (elements.length > 0) {
              const el = elements[Math.floor(rng() * elements.length)];
              await el.click({ timeout: 3000 }).catch(() => {});
            }
            actions.push({ type: actionType, result: 'survived', message: `Clicked random element (${elements.length} candidates)`, duration: performance.now() - t });
            break;
          }
          case 'invalid-input': {
            const inputs = await page.$$('input, textarea, select');
            if (inputs.length > 0) {
              const input = inputs[Math.floor(rng() * inputs.length)];
              const payloads = ['<script>alert(1)</script>', "'; DROP TABLE users;--", '🔥'.repeat(100), '\x00\x01\x02', 'a'.repeat(10000), '-1', '99999999999', '../../../etc/passwd'];
              const payload = payloads[Math.floor(rng() * payloads.length)];
              await input.fill(payload).catch(() => {});
              actions.push({ type: actionType, target: payload.slice(0, 30), result: 'survived', message: `Injected invalid input into form field`, duration: performance.now() - t });
            } else {
              actions.push({ type: actionType, result: 'survived', message: 'No input fields found', duration: performance.now() - t });
            }
            break;
          }
          case 'rapid-navigate': {
            const links = await page.$$eval('a[href]', (els: any[]) => els.map((el: any) => el.href).filter((h: string) => h && h.startsWith('http')));
            if (links.length > 0) {
              const link = links[Math.floor(rng() * links.length)];
              await page.goto(link, { timeout: 5000, waitUntil: 'domcontentloaded' }).catch(() => {});
              await page.goBack({ timeout: 3000 }).catch(() => {});
            }
            actions.push({ type: actionType, result: 'survived', message: 'Rapid navigate + back', duration: performance.now() - t });
            break;
          }
          case 'resize-viewport': {
            const widths = [320, 375, 768, 1024, 1440, 1920, 2560];
            const w = widths[Math.floor(rng() * widths.length)];
            await page.setViewportSize({ width: w, height: 800 });
            actions.push({ type: actionType, result: 'survived', message: `Resized to ${w}px`, duration: performance.now() - t });
            break;
          }
          case 'clear-storage': {
            await context.clearCookies();
            await page.evaluate('try { localStorage.clear(); sessionStorage.clear(); } catch(e) {}');
            actions.push({ type: actionType, result: 'survived', message: 'Cleared cookies + storage', duration: performance.now() - t });
            break;
          }
          case 'back-forward': {
            await page.goBack({ timeout: 3000 }).catch(() => {});
            await page.goForward({ timeout: 3000 }).catch(() => {});
            actions.push({ type: actionType, result: 'survived', message: 'Back + forward navigation', duration: performance.now() - t });
            break;
          }
          case 'scroll-extreme': {
            await page.evaluate('window.scrollTo(0, document.body.scrollHeight)');
            await page.evaluate('window.scrollTo(0, 0)');
            actions.push({ type: actionType, result: 'survived', message: 'Scrolled to bottom + top', duration: performance.now() - t });
            break;
          }
          case 'keyboard-spam': {
            await page.keyboard.press('Tab');
            await page.keyboard.press('Enter');
            await page.keyboard.press('Escape');
            await page.keyboard.type('test chaos input');
            actions.push({ type: actionType, result: 'survived', message: 'Tab + Enter + Escape + type', duration: performance.now() - t });
            break;
          }
          default: {
            actions.push({ type: actionType, result: 'survived', message: 'Action executed', duration: performance.now() - t });
          }
        }

        // Check if page crashed after each action
        try {
          await page.title();
        } catch {
          actions[actions.length - 1].result = 'crashed';
          // Try to recover
          await page.goto(baseUrl, { timeout: 5000 }).catch(() => {});
        }
      } catch (e: any) {
        actions.push({ type: actionType, result: 'error', message: e.message?.slice(0, 80) || 'Unknown error', duration: performance.now() - t });
      }
    }

    await browser.close();
  } catch (e: any) {
    spinner.fail('Chaos Agent crashed');
    out.error(e.message?.slice(0, 200) || 'Unknown error');
    process.exitCode = 2;
    return;
  }

  spinner.stop();
  const duration = performance.now() - start;

  const survived = actions.filter(a => a.result === 'survived').length;
  const crashed = actions.filter(a => a.result === 'crashed').length;
  const errors = actions.filter(a => a.result === 'error').length;
  const resilience = Math.round((survived / Math.max(actions.length, 1)) * 100);

  if (opts.json) {
    out.jsonOutput({ mode: 'chaos', actions, stats: { survived, crashed, errors, resilience, total: actions.length }, duration, url: baseUrl, seed });
    process.exitCode = crashed > 0 ? 2 : errors > 2 ? 1 : 0;
    return;
  }

  // Pretty output
  console.log(colors.bold('  CHAOS RESULTS'));
  console.log(colors.dim('  ' + '─'.repeat(48)));
  out.nl();

  for (const action of actions) {
    const icon = action.result === 'survived' ? colors.success('✓') : action.result === 'crashed' ? colors.error('💥') : colors.warning('⚠');
    const dur = colors.dim(`${Math.round(action.duration)}ms`);
    console.log(`  ${icon} ${colors.bold(action.type)} ${colors.dim('—')} ${action.message} ${dur}`);
  }

  out.nl();
  console.log(colors.dim('  ' + '━'.repeat(48)));
  out.nl();

  // Resilience score bar
  const barWidth = 40;
  const filledWidth = Math.round((resilience / 100) * barWidth);
  const barColor = resilience >= 90 ? colors.success : resilience >= 70 ? colors.warning : colors.error;
  console.log(`  Resilience  ${barColor('█'.repeat(filledWidth))}${colors.dim('░'.repeat(barWidth - filledWidth))}  ${colors.bold(String(resilience))}${colors.dim('%')}`);
  out.nl();

  console.log(`  ${colors.success(`${survived} survived`)} ${colors.dim('·')} ${crashed > 0 ? colors.error(`${crashed} crashed`) : colors.dim('0 crashed')} ${colors.dim('·')} ${errors > 0 ? colors.warning(`${errors} errors`) : colors.dim('0 errors')}`);
  out.nl();

  if (crashed > 0) {
    console.log(colors.error(`  ${icons.fail} App crashed ${crashed} time(s) during chaos testing.`));
    console.log(colors.dim(`  Run with --seed ${seed} to reproduce.`));
  } else if (resilience >= 90) {
    console.log(colors.success(`  ${icons.pass} Excellent resilience — app survived all chaos actions.`));
  } else {
    console.log(colors.warning(`  ${icons.warn} Some chaos actions caused issues. Review errors above.`));
  }

  out.nl();
  console.log(out.completedIn(duration));
  process.exitCode = crashed > 0 ? 2 : errors > 2 ? 1 : 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// STRESS MODE — Concurrent requests, rapid navigation, resource monitoring
// ═══════════════════════════════════════════════════════════════════════════

async function runStress(opts: RealityOpts): Promise<void> {
  const start = performance.now();
  const baseUrl = opts.url || 'http://localhost:3000';
  const concurrency = parseInt(opts.concurrency || '10');
  const testDuration = parseInt(opts.duration || '30') * 1000;

  out.brandHeader('Stress Test', 'Load & Performance');
  out.nl();
  console.log(`  ${colors.dim('Target:')}       ${colors.info(baseUrl)}`);
  console.log(`  ${colors.dim('Concurrency:')}  ${concurrency} virtual users`);
  console.log(`  ${colors.dim('Duration:')}     ${testDuration / 1000}s`);
  out.nl();

  const spinner = ora('Running stress test...').start();
  const results: StressResult[] = [];

  // Test 1: Concurrent homepage requests
  const t1 = performance.now();
  spinner.text = `Stress: ${concurrency} concurrent homepage requests...`;
  try {
    const promises = Array.from({ length: concurrency }, () =>
      fetch(baseUrl, { signal: AbortSignal.timeout(10000) })
        .then(r => ({ ok: r.ok, status: r.status, time: performance.now() - t1 }))
        .catch(e => ({ ok: false, status: 0, time: performance.now() - t1, error: e.message }))
    );
    const responses = await Promise.all(promises);
    const successCount = responses.filter(r => r.ok).length;
    const avgTime = Math.round(responses.reduce((s, r) => s + r.time, 0) / responses.length);
    const maxTime = Math.round(Math.max(...responses.map(r => r.time)));
    results.push({
      name: 'Concurrent homepage',
      status: successCount === concurrency ? 'passed' : successCount > concurrency * 0.8 ? 'warning' : 'failed',
      value: `${successCount}/${concurrency} OK, avg ${avgTime}ms, max ${maxTime}ms`,
      duration: performance.now() - t1,
    });
  } catch (e: any) {
    results.push({ name: 'Concurrent homepage', status: 'failed', value: e.message?.slice(0, 80), duration: performance.now() - t1 });
  }

  // Test 2: Rapid sequential requests
  const t2 = performance.now();
  spinner.text = 'Stress: rapid sequential requests...';
  let rapidCount = 0;
  let rapidErrors = 0;
  const rapidEnd = performance.now() + Math.min(testDuration, 10000);
  while (performance.now() < rapidEnd) {
    try {
      const r = await fetch(baseUrl, { signal: AbortSignal.timeout(5000) });
      if (r.ok) rapidCount++; else rapidErrors++;
    } catch { rapidErrors++; }
    if (rapidCount + rapidErrors > 200) break; // cap at 200
  }
  const rapidDuration = performance.now() - t2;
  const rps = Math.round((rapidCount / rapidDuration) * 1000);
  results.push({
    name: 'Rapid sequential',
    status: rapidErrors === 0 ? 'passed' : rapidErrors < 3 ? 'warning' : 'failed',
    value: `${rapidCount} OK, ${rapidErrors} errors, ~${rps} req/s`,
    duration: rapidDuration,
  });

  // Test 3: API endpoint stress
  const t3 = performance.now();
  spinner.text = 'Stress: API endpoint load...';
  const apiEndpoints = ['/api/health', '/api/status', '/api/v1/health'];
  let apiOk = 0;
  let apiFail = 0;
  for (const endpoint of apiEndpoints) {
    const apiPromises = Array.from({ length: Math.min(concurrency, 5) }, () =>
      fetch(`${baseUrl}${endpoint}`, { signal: AbortSignal.timeout(5000) })
        .then(r => r.ok)
        .catch(() => false)
    );
    const apiResults = await Promise.all(apiPromises);
    apiOk += apiResults.filter(Boolean).length;
    apiFail += apiResults.filter(r => !r).length;
  }
  results.push({
    name: 'API endpoint stress',
    status: apiFail === 0 ? 'passed' : apiFail < 3 ? 'warning' : 'failed',
    value: `${apiOk} OK, ${apiFail} failed across ${apiEndpoints.length} endpoints`,
    duration: performance.now() - t3,
  });

  // Test 4: Large payload
  const t4 = performance.now();
  spinner.text = 'Stress: large payload handling...';
  try {
    const largeBody = JSON.stringify({ data: 'x'.repeat(100000) });
    const r = await fetch(`${baseUrl}/api/health`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: largeBody,
      signal: AbortSignal.timeout(10000),
    });
    results.push({
      name: 'Large payload (100KB)',
      status: r.status < 500 ? 'passed' : 'failed',
      value: `HTTP ${r.status}`,
      duration: performance.now() - t4,
    });
  } catch (e: any) {
    results.push({
      name: 'Large payload (100KB)',
      status: e.message?.includes('ECONNREFUSED') ? 'failed' : 'warning',
      value: e.message?.slice(0, 60) || 'Error',
      duration: performance.now() - t4,
    });
  }

  // Test 5: Response time under load
  const t5 = performance.now();
  spinner.text = 'Stress: response time under load...';
  const timings: number[] = [];
  for (let i = 0; i < 20; i++) {
    const reqStart = performance.now();
    try {
      await fetch(baseUrl, { signal: AbortSignal.timeout(5000) });
      timings.push(performance.now() - reqStart);
    } catch { timings.push(5000); }
  }
  const p50 = percentile(timings, 50);
  const p95 = percentile(timings, 95);
  const p99 = percentile(timings, 99);
  results.push({
    name: 'Response time (20 reqs)',
    status: p95 < 2000 ? 'passed' : p95 < 5000 ? 'warning' : 'failed',
    value: `p50=${Math.round(p50)}ms, p95=${Math.round(p95)}ms, p99=${Math.round(p99)}ms`,
    duration: performance.now() - t5,
  });

  spinner.stop();
  const duration = performance.now() - start;

  if (opts.json) {
    out.jsonOutput({ mode: 'stress', results, stats: { concurrency, durationMs: Math.round(duration), rps }, url: baseUrl });
    process.exitCode = results.some(r => r.status === 'failed') ? 2 : results.some(r => r.status === 'warning') ? 1 : 0;
    return;
  }

  // Pretty output
  console.log(colors.bold('  STRESS RESULTS'));
  console.log(colors.dim('  ' + '─'.repeat(48)));
  out.nl();

  for (const result of results) {
    const icon = result.status === 'passed' ? colors.success(icons.success) : result.status === 'failed' ? colors.error(icons.error) : colors.warning(icons.warning);
    const dur = colors.dim(`${Math.round(result.duration)}ms`);
    console.log(`  ${icon} ${colors.bold(result.name)}`);
    console.log(`    ${result.value} ${dur}`);
  }

  out.nl();
  console.log(colors.dim('  ' + '━'.repeat(48)));
  out.nl();

  const passed = results.filter(r => r.status === 'passed').length;
  const failed = results.filter(r => r.status === 'failed').length;
  const warned = results.filter(r => r.status === 'warning').length;
  console.log(`  ${colors.success(`${passed} passed`)} ${colors.dim('·')} ${failed > 0 ? colors.error(`${failed} failed`) : colors.dim('0 failed')} ${colors.dim('·')} ${warned > 0 ? colors.warning(`${warned} warnings`) : colors.dim('0 warnings')}`);
  console.log(`  ${colors.dim(`~${rps} req/s`)} ${colors.dim('·')} ${colors.dim(`p95=${Math.round(p95)}ms`)}`);
  out.nl();
  console.log(out.completedIn(duration));

  process.exitCode = failed > 0 ? 2 : warned > 0 ? 1 : 0;
}

// ═══════════════════════════════════════════════════════════════════════════
// HTTP FALLBACKS
// ═══════════════════════════════════════════════════════════════════════════

async function runHttpChecks(baseUrl: string, opts: RealityOpts): Promise<void> {
  const checks: RealityCheck[] = [];
  const endpoints = ['/', '/api/health'];
  for (const endpoint of endpoints) {
    const t = performance.now();
    try {
      const response = await fetch(`${baseUrl}${endpoint}`, { signal: AbortSignal.timeout(5000) });
      checks.push({ name: `HTTP ${endpoint}`, status: response.ok ? 'passed' : response.status === 404 ? 'warning' : 'failed', duration: performance.now() - t, message: `${response.status} ${response.statusText}` });
    } catch (e: any) {
      checks.push({ name: `HTTP ${endpoint}`, status: 'failed', duration: performance.now() - t, message: e.message?.includes('ECONNREFUSED') ? 'Server not running' : e.message?.slice(0, 80) });
    }
  }
  if (opts.json) { out.jsonOutput({ checks, url: baseUrl, mode: 'http-only' }); return; }
  for (const check of checks) { console.log(out.checkLine(check.status, check.name, colors.dim(check.message || ''))); }
  out.nl();
}

async function runHttpChaos(baseUrl: string, maxActions: number, opts: RealityOpts): Promise<void> {
  const actions: ChaosAction[] = [];
  const payloads = ['<script>alert(1)</script>', "'; DROP TABLE users;--", 'a'.repeat(50000), '../../../etc/passwd', '{"__proto__":{"admin":true}}'];

  for (let i = 0; i < Math.min(maxActions, 10); i++) {
    const payload = payloads[i % payloads.length];
    const t = performance.now();
    try {
      const r = await fetch(baseUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ input: payload }), signal: AbortSignal.timeout(5000) });
      actions.push({ type: 'http-inject', target: payload.slice(0, 30), result: r.status < 500 ? 'survived' : 'error', message: `HTTP ${r.status}`, duration: performance.now() - t });
    } catch (e: any) {
      actions.push({ type: 'http-inject', target: payload.slice(0, 30), result: 'error', message: e.message?.slice(0, 60) || 'Error', duration: performance.now() - t });
    }
  }

  if (opts.json) { out.jsonOutput({ mode: 'http-chaos', actions, url: baseUrl }); return; }
  for (const a of actions) {
    const icon = a.result === 'survived' ? colors.success('✓') : colors.error('✗');
    console.log(`  ${icon} ${a.type}: ${a.message} ${colors.dim(`${Math.round(a.duration)}ms`)}`);
  }
  out.nl();
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function renderChecks(checks: RealityCheck[], baseUrl: string, duration: number): void {
  console.log(`  Testing against ${colors.info(baseUrl)}`);
  out.nl();
  for (const check of checks) {
    const dur = `${Math.round(check.duration)}ms`;
    const detail = check.message ? `${check.message} (${dur})` : dur;
    console.log(out.checkLine(check.status, check.name, colors.dim(detail)));
    if (check.screenshot) console.log(`     ${colors.dim(icons.arrow)} Screenshot: ${check.screenshot}`);
  }
  out.nl();
  console.log(out.divider());
  const passed = checks.filter(c => c.status === 'passed').length;
  const failed = checks.filter(c => c.status === 'failed').length;
  const warned = checks.filter(c => c.status === 'warning').length;
  console.log(`  ${passed} passed ${colors.dim('·')} ${failed} failed ${colors.dim('·')} ${warned} warnings`);
  console.log(out.completedIn(duration));
  out.nl();
  process.exitCode = failed > 0 ? 2 : warned > 0 ? 1 : 0;
}

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => { s = (s * 1664525 + 1013904223) & 0xFFFFFFFF; return (s >>> 0) / 0xFFFFFFFF; };
}

function percentile(arr: number[], p: number): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, idx)];
}

async function runList(opts: GlobalOptions): Promise<void> {
  out.brandHeader('Reality Modes', 'Available modes and flows');
  out.nl();

  console.log(colors.bold('  Modes:'));
  console.log(`    ${icons.globe} ${colors.bold('test')}     — Standard runtime checks (homepage, console, links, images, API)`);
  console.log(`    ${icons.fire} ${colors.bold('chaos')}    — Chaos Agent: random clicks, invalid inputs, rapid navigation`);
  console.log(`    ${icons.lightning} ${colors.bold('stress')}   — Stress test: concurrent users, rapid requests, response times`);
  out.nl();

  console.log(colors.bold('  Test Flows:'));
  console.log(`    ${colors.dim('homepage')}     — Loads homepage, checks HTTP status`);
  console.log(`    ${colors.dim('console')}      — Checks for console errors`);
  console.log(`    ${colors.dim('links')}        — Validates navigation links`);
  console.log(`    ${colors.dim('images')}       — Checks all images load`);
  console.log(`    ${colors.dim('api-health')}   — Checks /api/health endpoint`);
  out.nl();

  console.log(colors.bold('  Chaos Actions:'));
  console.log(`    ${colors.dim('random-click')}    — Click random interactive elements`);
  console.log(`    ${colors.dim('invalid-input')}   — Inject XSS, SQL injection, huge strings`);
  console.log(`    ${colors.dim('rapid-navigate')}  — Rapid page navigation + back`);
  console.log(`    ${colors.dim('resize-viewport')} — Random viewport sizes (320px–2560px)`);
  console.log(`    ${colors.dim('clear-storage')}   — Clear cookies + localStorage`);
  console.log(`    ${colors.dim('keyboard-spam')}   — Tab, Enter, Escape, random typing`);
  out.nl();

  console.log(colors.bold('  Examples:'));
  console.log(`    ${colors.info('vibecheck reality test')}                  ${colors.dim('# Standard checks')}`);
  console.log(`    ${colors.info('vibecheck reality chaos --actions 50')}    ${colors.dim('# 50 chaos actions')}`);
  console.log(`    ${colors.info('vibecheck reality stress --concurrency 20')} ${colors.dim('# 20 virtual users')}`);
  console.log(`    ${colors.info('vibecheck reality chaos --seed 42')}      ${colors.dim('# Reproducible chaos')}`);
  out.nl();
}
